import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded, invokeWithAccessToken } from '~/AccessToken';
import { findReferenceProductTypes, ReferencedProductTypesResult } from '~/Api/RoPriceReferencing/MaxPrice';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExport as apiCreateExport,
	ErrorResponse,
	ExportResult,
	ExportType,
	findRoMaxPrice,
	RoMaxPriceResult,
	Update,
	updateRoMaxPrice as apiUpdateRoMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/RoMaxPrice';
import { find as findUserSettings, Settings, update as apiUpdateUserSettings } from '~/Api/User/RoMaxPriceSettings';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';
import resetProductGroup from '~/Api/User/ProductGroup/RoMaxPrice/resetGroup';
import { showAllProductsAction } from '~/Product';
import { resetRoMaxPriceList } from '~/libs/infrastructure/api/users/roMaxPrice';

const updateQueue = new PromiseQueue(1, Infinity);

export const fetchRoMaxPrice =
	(groupId: string): ThunkAction<Promise<RoMaxPriceResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findRoMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				groupId,
				roMaxPrice: response?.roMaxPrice ?? null,
				request,
				type: ActionType.receive,
			})
		);
	};

export const immediatelyUpdateRoMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateRoMaxPrice, userId, groupId, update))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				groupId,
				roMaxPrice: response?.roMaxPrice ?? null,
				request,
				type: ActionType.receiveUpdate,
			})
		);
	};

export const updateRoMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateRoMaxPrice(groupId, update)));
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};

export const handleRoMaxPriceUpdateResponse =
	<Response>(response: Response, groupId: string): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}
		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchRoMaxPrice(groupId)).catch(handleError);
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchRoMaxPrice(groupId)).catch(handleError);
		}
		dispatch(handleError(response));
	};

export const createExport =
	(groupId: string, type: ExportType): ThunkAction<Promise<ExportResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(apiCreateExport, userId, groupId, type));
		dispatch({ type: ActionType.requestExport, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				request,
				type: ActionType.receiveExport,
				validationResult:
					error !== null
						? error instanceof ErrorResponse
							? error.toValidationResult()
							: null
						: new ValidationResult(null, false, [], []),
			})
		);
	};

export const fetchUserSettings = (): ThunkAction<Promise<void>, Action> => async (dispatch) => {
	const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
	const request = dispatch(invokeApi(findUserSettings, userId));
	dispatch({ type: ActionType.requestUserSettings, request });

	await resolve(request, (response) =>
		dispatch({
			settings: response?.roMaxPriceSettings ?? null,
			request,
			type: ActionType.receiveUserSettings,
		})
	);
};

export const updateUserSettings =
	(settings: Settings): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(apiUpdateUserSettings, userId, settings));
		dispatch({ type: ActionType.requestUserSettingsUpdate, request });

		await resolve(request, (response) =>
			dispatch({
				settings: response?.roMaxPriceSettings ?? null,
				request,
				type: ActionType.receiveUserSettingsUpdate,
			})
		);
	};

export function fetchReferencedProductTypes(): ThunkAction<Promise<ReferencedProductTypesResult>, Action> {
	return async (dispatch, getState) => {
		const { productTypes } = getState().roMaxPrice;
		if (productTypes !== null) {
			return productTypes.request;
		}

		const request = dispatch(invokeApi(findReferenceProductTypes));
		dispatch({ type: ActionType.requestProductTypes, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveProductTypes,
				request,
				types: response?.items || null,
			})
		);
	};
}

export const pruneRoMaxPrice = (groupId: string): Action => ({
	type: ActionType.prune,
	groupId,
});

export const scheduleRoMaxPriceUpdate =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) =>
		new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateRoMaxPrice(groupId, update)));
			}, 2000);

			dispatch({
				groupId,
				timeoutId,
				type: ActionType.scheduleUpdate,
				update,
			});
		});

export const commitRoMaxPriceUpdate =
	(groupId: string): ThunkAction<Promise<void>, Action> =>
	async (dispatch, getState) => {
		const { update } = getState().roMaxPrice;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, data } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateRoMaxPrice(groupId, data));
		}
	};

export const handleCommitError =
	<Error>(error: Error): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};

export const resetRoMaxPriceGroup =
	(groupId: string): ThunkAction<Promise<void>, any> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;

		resetRoMaxPriceList(userId, groupId)
			.then(() => {
				dispatch(fetchRoMaxPrice(groupId))
					.then(() => {
						dispatch(showAllProductsAction(false));
					})
					.catch((error) => {
						handleError(error);
					});
			})
			.catch((error) => {
				handleError(error);
			});
	};
